import { useCallback, useEffect, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { DataStateRow } from "@/components/ui/data-state";
import { toast } from "sonner";
import { Plus, Download, RefreshCcw, Clock, CheckCircle, XCircle, Ban } from "lucide-react";

const STATUS_CONFIG = {
  pending:   { label: "Chờ duyệt",    icon: Clock,         color: "text-amber-600" },
  approved:  { label: "Đã duyệt",     icon: CheckCircle,   color: "text-green-600" },
  rejected:  { label: "Từ chối",      icon: XCircle,       color: "text-red-600" },
  cancelled: { label: "Đã huỷ",       icon: Ban,           color: "text-stone-400" },
  fulfilled: { label: "Đã cấp phát",  icon: CheckCircle,   color: "text-blue-600" },
};

// ---------------------------------------------------------------------------
// New request dialog
// ---------------------------------------------------------------------------
function NewRequestDialog({ open, onClose, onCreated }) {
  const [entities, setEntities] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [entityId, setEntityId] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [purpose, setPurpose] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/legal/entities").then((r) => setEntities(r.data || [])).catch(() => {});
    setEntityId(""); setDocTypes([]); setSelectedTypes([]); setPurpose("");
  }, [open]);

  useEffect(() => {
    if (!entityId) { setDocTypes([]); return; }
    api.get(`/legal/document-types?entity_id=${entityId}`)
      .then((r) => setDocTypes((r.data || []).filter((dt) => dt.is_requestable)))
      .catch(() => {});
  }, [entityId]);

  const toggleType = (dtId) => {
    setSelectedTypes((prev) =>
      prev.includes(dtId) ? prev.filter((id) => id !== dtId) : [...prev, dtId],
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!entityId) { toast.error("Chọn pháp nhân"); return; }
    if (!selectedTypes.length) { toast.error("Chọn ít nhất 1 loại giấy tờ"); return; }
    if (!purpose.trim()) { toast.error("Nhập mục đích yêu cầu"); return; }
    setSaving(true);
    try {
      await api.post("/legal/requests", {
        entity_id: entityId,
        purpose: purpose.trim(),
        document_type_ids: selectedTypes,
      });
      toast.success("Đã gửi yêu cầu hồ sơ pháp lý");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, "Không gửi được yêu cầu").message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Yêu cầu hồ sơ pháp lý</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2 overflow-y-auto flex-1 pr-1">
          <div>
            <Label>Pháp nhân</Label>
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger data-testid="legal-request-entity">
                <SelectValue placeholder="Chọn pháp nhân…" />
              </SelectTrigger>
              <SelectContent>
                {entities.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {entityId && (
            <div>
              <Label>Loại giấy tờ cần yêu cầu</Label>
              {docTypes.length === 0
                ? <p className="text-sm text-stone-400 mt-1">Không có loại giấy tờ khả dụng</p>
                : docTypes.map((dt) => (
                  <label key={dt.id} className="flex items-center gap-2 mt-1.5 text-sm cursor-pointer"
                    data-testid={`legal-request-doctype-${dt.id}`}>
                    <input type="checkbox" checked={selectedTypes.includes(dt.id)}
                      onChange={() => toggleType(dt.id)}
                      data-testid={`legal-request-doctype-cb-${dt.id}`} />
                    <span>{dt.name}</span>
                    <span className="text-stone-400 text-xs font-mono">{dt.code}</span>
                  </label>
                ))
              }
            </div>
          )}

          <div>
            <Label>Mục đích sử dụng</Label>
            <Textarea required rows={3} value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Mô tả lý do cần hồ sơ pháp lý này…"
              data-testid="legal-request-purpose" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Huỷ</Button>
            <Button type="submit" disabled={saving} data-testid="legal-request-submit">{saving ? "Đang gửi…" : "Gửi yêu cầu"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main staff page
// ---------------------------------------------------------------------------
export default function StaffLegalRequest() {
  const [requests, setRequests] = useState([]);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newDialog, setNewDialog] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [reqRes, grantRes] = await Promise.all([
        api.get("/legal/requests"),
        api.get("/legal/my-grants"),
      ]);
      setRequests(reqRes.data || []);
      setGrants(grantRes.data || []);
    } catch (e) {
      setError(parseApiError(e, "Không tải được dữ liệu").message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelRequest = async (reqId) => {
    try {
      await api.post(`/legal/requests/${reqId}/cancel`, {});
      toast.success("Đã huỷ yêu cầu");
      load();
    } catch (err) {
      toast.error(parseApiError(err, "Không huỷ được").message);
    }
  };

  const downloadGrant = async (grant) => {
    setDownloading(grant.id);
    try {
      const resp = await api.get(`/legal/grants/${grant.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = grant.file_name || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(parseApiError(err, "Không tải được tài liệu").message);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="p-4 space-y-6" data-testid="page-staff-legal-request">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-stone-800">Hồ sơ pháp lý</h1>
          <p className="text-sm text-stone-500">Yêu cầu tài liệu pháp nhân và tải về khi được duyệt</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={load}>
            <RefreshCcw className="w-4 h-4 mr-1" />Tải lại
          </Button>
          <Button size="sm" onClick={() => setNewDialog(true)} data-testid="legal-request-open-dialog">
            <Plus className="w-4 h-4 mr-1" />Tạo yêu cầu
          </Button>
        </div>
      </div>

      {loading && <DataStateRow loading />}
      {error && <div className="text-red-600 text-sm">{error}</div>}

      {/* Active grants */}
      {grants.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-stone-700 mb-2">Tài liệu được phép tải ({grants.length})</h2>
          <div className="space-y-2">
            {grants.map((grant) => (
              <div key={grant.id}
                className="bg-green-50 border border-green-200 rounded-sm px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-sm text-stone-800">{grant.file_name}</div>
                  <div className="text-xs text-stone-500">
                    {grant.entity_name} — {grant.document_type_name} — v{grant.version_no}
                  </div>
                  <div className="text-xs text-stone-400 mt-0.5">
                    Hết hạn: {new Date(grant.expires_at).toLocaleString("vi-VN")}
                  </div>
                </div>
                <Button size="sm" onClick={() => downloadGrant(grant)} disabled={downloading === grant.id}>
                  <Download className="w-3.5 h-3.5 mr-1" />
                  {downloading === grant.id ? "Đang tải…" : "Tải về"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Request history */}
      {!loading && (
        <div>
          <h2 className="text-sm font-semibold text-stone-700 mb-2">Lịch sử yêu cầu</h2>
          {requests.length === 0
            ? <p className="text-sm text-stone-400 py-4 text-center">Chưa có yêu cầu nào</p>
            : (
              <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 text-stone-500 text-xs bg-stone-50">
                      <th className="px-4 py-2 text-left">Pháp nhân</th>
                      <th className="px-4 py-2 text-left">Mục đích</th>
                      <th className="px-4 py-2 text-left">Ngày tạo</th>
                      <th className="px-4 py-2 text-left">Trạng thái</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((req) => {
                      const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                      const Icon = cfg.icon;
                      return (
                        <tr key={req.id} className="border-b border-stone-50 hover:bg-stone-50">
                          <td className="px-4 py-3 font-medium">{req.entity_name}</td>
                          <td className="px-4 py-3 max-w-[200px] truncate">{req.purpose}</td>
                          <td className="px-4 py-3 text-xs text-stone-400 whitespace-nowrap">
                            {new Date(req.created_at).toLocaleDateString("vi-VN")}
                          </td>
                          <td className="px-4 py-3">
                            <div className={`flex items-center gap-1 text-xs font-medium ${cfg.color}`}>
                              <Icon className="w-3.5 h-3.5" />
                              {cfg.label}
                            </div>
                            {req.status === "rejected" && req.reject_reason && (
                              <div className="text-xs text-stone-400 mt-0.5">{req.reject_reason}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {req.status === "pending" && (
                              <Button size="sm" variant="ghost" className="text-stone-400 hover:text-red-600"
                                onClick={() => cancelRequest(req.id)}>
                                Huỷ
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      )}

      <NewRequestDialog
        open={newDialog}
        onClose={() => setNewDialog(false)}
        onCreated={load}
      />
    </div>
  );
}
